'use client';

import { Button } from '~/components/ui/button';
import { Form } from '../ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '~/trpc/client';
import { FormInput } from '../forms/form-input';
import {
  type NewsLetterSchema,
  newsLetterSchema,
} from '~/validation-schemas/news-letter';
import { toast } from 'sonner';
import { isTRPCClientError } from '~/trpc/is-trpc-error';
import { cn } from '~/lib/utils';
import { type CallToActionButtonStyleReturn } from './call-to-action';

export function BlockNewsletterSignup({
  className,
  customButton,
}: {
  className?: string;
  customButton: CallToActionButtonStyleReturn;
}) {
  const addUserToNewsLetter = trpc.formRouter.addUserToNewsLetter.useMutation();

  const form = useForm<NewsLetterSchema>({
    resolver: zodResolver(newsLetterSchema),
    defaultValues: {
      name: '',
      email: '',
    },
  });

  const onSubmit = async (data: NewsLetterSchema) => {
    if (addUserToNewsLetter.isPending) return;
    try {
      await toast
        .promise(addUserToNewsLetter.mutateAsync(data), {
          loading: 'Adding user to newsletter...',
          success: () => {
            return `Successfully added to newsletter`;
          },
          error: (error) => {
            if (isTRPCClientError(error)) {
              return error.message;
            }

            return `Error sharing basket`;
          },
        })
        .unwrap();
      form.reset();
      // setOpen(false);
    } catch (error) {
      console.error('Error adding user to newsletter:', error);
    }
  };

  const isDisabled = form.formState.isSubmitting;

  return (
    <div className={cn('w-full sm:mx-auto sm:max-w-3xl', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-2 px-2 sm:grid-cols-2 sm:gap-4">
            <div className="col-span-1">
              <FormInput
                id="name"
                name="name"
                label="Name"
                control={form.control}
                disabled={isDisabled}
                className=""
                placeholder="Enter your name"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="email"
                name="email"
                label="Email"
                control={form.control}
                disabled={isDisabled}
                className=""
                placeholder="Enter your email address"
              />
            </div>
            <div className="col-span-1 mt-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={isDisabled}
                style={customButton?.customButtonStyle}
                className={cn(
                  'mx-auto block w-full sm:w-1/2',
                  customButton?.customButtonClasses,
                )}
                variant={customButton ? 'custom' : 'default'}
              >
                Subscribe to newsletter
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
